import React, {useState} from 'react';
import {CButton, CInput, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CSelect, CTextarea} from '@coreui/react';
import {ACAO_SITUACAO, EtapaProcesso, Processo, SituacaoProcesso, TipoItem, dataBr} from "./modelo";
import {Executar} from "./processoTela";

// ---------------------------------------------------------------- situacao

interface SituacaoProps {
    processo: Processo,
    executar: Executar,
    onFechar: () => void,
}

const AVISO: {[s: number]: string} = {
    [SituacaoProcesso.CONGELADO]: 'O processo fica parado: não aceita movimentos e não conta atraso.',
    [SituacaoProcesso.CANCELADO]: 'O processo é encerrado sem concluir. Pode ser reaberto pelo gestor.',
    [SituacaoProcesso.ARQUIVADO]: 'Encerra o processo guardando a lista dos itens que ficaram pendentes.',
    [SituacaoProcesso.FINALIZADO]: 'Só com todos os itens concluídos ou declinados.',
};

export const SituacaoModal = ({processo, executar, onFechar}: SituacaoProps) => {
    const [nova, setNova] = useState<SituacaoProcesso | null>(processo.transicoes.length === 1 ? processo.transicoes[0] : null);
    const [motivo, setMotivo] = useState('');
    const [deslocar, setDeslocar] = useState(true);
    const descongelando = processo.situacao === SituacaoProcesso.CONGELADO && nova === SituacaoProcesso.EXECUTANDO;

    const confirmar = () => {
        executar(`processo/situacao/${processo.id}`, {situacao: nova, motivo, deslocar_prazos: deslocar}).then(ok => ok && onFechar());
    };

    return (
        <CModal show onClose={onFechar} centered>
            <CModalHeader closeButton><CModalTitle>Situação do processo</CModalTitle></CModalHeader>
            <CModalBody>
                <p className="pr-ajuda">Hoje: <strong>{processo.situacao_nome}</strong>{processo.congelado_em ? ` desde ${dataBr(processo.congelado_em)}` : ''}.</p>
                <div className="pr-situacoes">
                    {processo.transicoes.map(s => (
                        <button type="button" key={s} className={nova === s ? 'ativa' : ''} onClick={() => setNova(s)}>
                            {s === SituacaoProcesso.EXECUTANDO && processo.situacao === SituacaoProcesso.ABERTO ? 'Iniciar' : ACAO_SITUACAO[s]}
                        </button>
                    ))}
                </div>
                {nova !== null && AVISO[nova] && <p className="pr-ajuda">{AVISO[nova]}</p>}
                {descongelando && (
                    <label className="pr-opcao pequena">
                        <input type="checkbox" checked={deslocar} onChange={e => setDeslocar(e.target.checked)}/>
                        Deslocar os prazos pendentes pelos dias úteis em que ficou congelado
                    </label>
                )}
                <CTextarea rows={3} className="mt-2" value={motivo} onChange={(e: any) => setMotivo(e.target.value)} placeholder="Motivo"/>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" variant="outline" onClick={onFechar}>Cancelar</CButton>
                <CButton color="primary" disabled={nova === null} onClick={confirmar}>Confirmar</CButton>
            </CModalFooter>
        </CModal>
    );
};

// ---------------------------------------------------------------- etapa

interface EtapaProps {
    processo: Processo,
    etapa: EtapaProcesso,
    modo: 'transferir' | 'avulso',
    usuarios: Array<{id: number, nome: string}>,
    executar: Executar,
    onFechar: () => void,
}

export const EtapaModal = ({processo, etapa, modo, usuarios, executar, onFechar}: EtapaProps) => {
    const [destino, setDestino] = useState('');
    const [motivo, setMotivo] = useState('');
    const [levar, setLevar] = useState(true);
    const [item, setItem] = useState({titulo: '', orientacao: '', tipo: TipoItem.CHECK, prazo: etapa.previsao_fim, papel_id: etapa.papel_id, acao_cliente: '', opcoes: ''});

    const confirmar = () => {
        if (modo === 'transferir') {
            const [tipo, id] = destino.split(':');
            executar(`processo/etapa/transferir/${etapa.id}`, {[tipo === 'p' ? 'papel_id' : 'usuario_id']: Number(id), motivo, levar_itens: levar})
                .then(ok => ok && onFechar());
        } else {
            const opcoes = item.opcoes.split('\n').map(rotulo => rotulo.trim()).filter(Boolean).map(rotulo => ({rotulo}));
            executar(`processo/etapa/item-avulso/${etapa.id}`, {...item, opcoes}).then(ok => ok && onFechar());
        }
    };

    const selecao = item.tipo === TipoItem.SELECAO || item.tipo === TipoItem.SELECAO_MULTIPLA;
    const papelAtual = processo.papeis.find(p => p.id === etapa.papel_id);

    return (
        <CModal show onClose={onFechar} centered>
            <CModalHeader closeButton>
                <CModalTitle>{modo === 'transferir' ? 'Transferir etapa' : 'Item avulso'} · {etapa.nome}</CModalTitle>
            </CModalHeader>
            <CModalBody className="pr-form">
                {modo === 'transferir' ? (
                    <>
                        <p className="pr-ajuda">Hoje: {papelAtual?.pessoa || 'sem responsável'} ({papelAtual?.nome}).</p>
                        <CSelect value={destino} onChange={(e: any) => setDestino(e.target.value)}>
                            <option value="">Para quem?</option>
                            <optgroup label="Papéis do processo">
                                {processo.papeis.filter(p => p.id !== etapa.papel_id).map(p => (
                                    <option key={p.id} value={`p:${p.id}`}>{p.nome}{p.pessoa ? ` — ${p.pessoa}` : ''}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Direto para uma pessoa">
                                {usuarios.map(u => <option key={u.id} value={`u:${u.id}`}>{u.nome}</option>)}
                            </optgroup>
                        </CSelect>
                        <label className="pr-opcao pequena">
                            <input type="checkbox" checked={levar} onChange={e => setLevar(e.target.checked)}/> Levar junto os itens pendentes do responsável atual
                        </label>
                        <CTextarea rows={3} value={motivo} onChange={(e: any) => setMotivo(e.target.value)} placeholder="Motivo (obrigatório)"/>
                    </>
                ) : (
                    <>
                        <p className="pr-ajuda">Item só deste processo; não muda o fluxo.</p>
                        <CInput placeholder="Título" value={item.titulo} maxLength={200} onChange={(e: any) => setItem({...item, titulo: e.target.value})}/>
                        <CTextarea rows={2} placeholder="Orientação" value={item.orientacao} onChange={(e: any) => setItem({...item, orientacao: e.target.value})}/>
                        <div className="pr-form-linha">
                            <CSelect value={item.tipo} onChange={(e: any) => setItem({...item, tipo: Number(e.target.value)})}>
                                <option value={TipoItem.CHECK}>Check</option>
                                <option value={TipoItem.SELECAO}>Seleção única</option>
                                <option value={TipoItem.SELECAO_MULTIPLA}>Seleção múltipla</option>
                                <option value={TipoItem.TEXTO}>Texto</option>
                                <option value={TipoItem.ARQUIVO}>Arquivo</option>
                                <option value={TipoItem.DATA}>Data</option>
                            </CSelect>
                            <CInput type="date" value={item.prazo} onChange={(e: any) => setItem({...item, prazo: e.target.value})}/>
                        </div>
                        {selecao && (
                            <CTextarea rows={3} placeholder="Opções, uma por linha" value={item.opcoes} onChange={(e: any) => setItem({...item, opcoes: e.target.value})}/>
                        )}
                        <CSelect value={item.papel_id} onChange={(e: any) => setItem({...item, papel_id: Number(e.target.value)})}>
                            {processo.papeis.map(p => <option key={p.id} value={p.id}>{p.nome}{p.pessoa ? ` — ${p.pessoa}` : ''}</option>)}
                        </CSelect>
                        <CTextarea rows={2} placeholder="Pedido ao cliente (opcional)" value={item.acao_cliente} onChange={(e: any) => setItem({...item, acao_cliente: e.target.value})}/>
                    </>
                )}
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" variant="outline" onClick={onFechar}>Cancelar</CButton>
                <CButton color="primary" onClick={confirmar}
                         disabled={modo === 'transferir' ? (!destino || !motivo.trim()) : !item.titulo.trim()}>Confirmar</CButton>
            </CModalFooter>
        </CModal>
    );
};

// ---------------------------------------------------------------- reprogramar etapa

interface ReprogramarProps {
    etapa: EtapaProcesso,
    executar: Executar,
    onFechar: () => void,
}

/**
 * Novo inicio e/ou nova duracao. A meta original continua guardada; com
 * cascata, as etapas seguintes sao empurradas pela regra do fluxo.
 */
export const ReprogramarEtapaModal = ({etapa, executar, onFechar}: ReprogramarProps) => {
    const [inicio, setInicio] = useState(etapa.previsao_inicio);
    const [duracao, setDuracao] = useState<number>(etapa.duracao_dias_uteis);
    const [cascata, setCascata] = useState(true);
    const [motivo, setMotivo] = useState('');

    const confirmar = () => {
        executar(`processo/etapa/reprogramar/${etapa.id}`, {inicio, duracao, cascata, motivo}).then(ok => ok && onFechar());
    };

    return (
        <CModal show onClose={onFechar} centered>
            <CModalHeader closeButton><CModalTitle>Reprogramar · {etapa.nome}</CModalTitle></CModalHeader>
            <CModalBody className="pr-form">
                <p className="pr-ajuda">
                    Hoje: {dataBr(etapa.previsao_inicio)} → {dataBr(etapa.previsao_fim)} ({etapa.duracao_dias_uteis} dias úteis).
                    Meta original: {dataBr(etapa.previsao_inicio_original)} → {dataBr(etapa.previsao_fim_original)}.
                </p>
                <div className="pr-form-linha">
                    <label><span>Novo início</span><CInput type="date" value={inicio} onChange={(e: any) => setInicio(e.target.value)}/></label>
                    <label><span>Duração (dias úteis)</span><CInput type="number" min={1} max={365} value={duracao} onChange={(e: any) => setDuracao(Number(e.target.value))}/></label>
                </div>
                <label className="pr-opcao pequena">
                    <input type="checkbox" checked={cascata} onChange={e => setCascata(e.target.checked)}/> Empurrar as etapas seguintes (cascata)
                </label>
                <p className="pr-ajuda">Os itens pendentes andam junto, mantendo a distância do início da etapa. Itens resolvidos não mudam.</p>
                <CTextarea rows={3} placeholder="Motivo (obrigatório)" value={motivo} onChange={(e: any) => setMotivo(e.target.value)}/>
            </CModalBody>
            <CModalFooter>
                <CButton color="dark" variant="outline" onClick={onFechar}>Cancelar</CButton>
                <CButton color="primary" disabled={motivo.trim().length < 5 || !inicio || duracao < 1} onClick={confirmar}>Reprogramar</CButton>
            </CModalFooter>
        </CModal>
    );
};
